"use client";

import { useEffect, useRef, useState } from "react";
import { getUploadUrl } from "@/services/upload.client";

const HTTP_URL_PATTERN = /^https?:\/\//i;
const ORG_LOGO_KEY_PREFIX = "organizations/";

/**
 * Presigns an S3 `organizations/*` logo key via `getUploadUrl`. Already-full
 * http(s) URLs pass through untouched (matches the legacy stores' checks in
 * `src/app/settings/general/page.tsx` / `src/stores/organization.ts`).
 * Anything else (empty, or a bare key that isn't an org logo) is
 * `undefined` — callers fall back to a default avatar themselves.
 *
 * The presigned URL is cached per key for the hook instance's lifetime (a
 * `useRef` map — resets on unmount, never shared across hook instances).
 */
export function useSignedLogo(logoKey?: string): string | undefined {
  const cacheRef = useRef<Map<string, string>>(new Map());
  const [signedUrl, setSignedUrl] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!logoKey || !logoKey.startsWith(ORG_LOGO_KEY_PREFIX)) {
      setSignedUrl(undefined);
      return;
    }

    const cached = cacheRef.current.get(logoKey);
    if (cached) {
      setSignedUrl(cached);
      return;
    }

    let cancelled = false;
    getUploadUrl(logoKey)
      .then((url) => {
        cacheRef.current.set(logoKey, url);
        if (!cancelled) setSignedUrl(url);
      })
      .catch(() => {
        if (!cancelled) setSignedUrl(undefined);
      });

    return () => {
      cancelled = true;
    };
  }, [logoKey]);

  if (!logoKey) return undefined;
  if (HTTP_URL_PATTERN.test(logoKey)) return logoKey;
  if (!logoKey.startsWith(ORG_LOGO_KEY_PREFIX)) return undefined;
  return signedUrl;
}
